import { ComponentContainer } from "./ComponentContainer";
import { ComponentHeader } from "./ComponentHeader";
import React, { Fragment, useEffect, useRef, useState } from "react";
import Leaderboard from "./Leaderboard";
import Discussion from "./Discussion";
import Entry from "./Entry";
import { API_URL } from "../lib/constants";
import { useSession } from "next-auth/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getUserEntry } from "../api/entries";
import { useRouter } from "next/router";
import Link from "next/link";
import { isEmpty, filter } from "lodash";
import {
  formatDistance,
  formatDistanceToNowStrict,
  isAfter,
  parseISO,
} from "date-fns";
import {
  approveUserPoolJoinRequest,
  getPoolById,
  rejectUserPoolJoinRequest,
} from "../api/pools";
import { rejectedIcon, unapprovedIcon } from "./Icons/Icons";
import toast from "react-hot-toast";
import Spinner from "./Icons/Spinner";
import GolfSpinner from "./Icons/GolfSpinner";
import { formatScore, ordinalValue } from "../lib/helpers";
import Refresh from "./Icons/Refresh";
import Head from "next/head";
import LeaderboardAdSlot from "./Adverts/LeaderboardAdSlot";
import BlockAdSlot from "./Adverts/BlockAdSlot";
import ImageWithFallback from "./ImageWithFallback";

const makeEntryButton = (poolId) => (
  <div className="text-right mt-2 mb-2 flex justify-end">
    <Link href={`/make_entry?poolId=${poolId as string}`}>
      <a className="text-white bg-red-400 font-bold text-[12px] px-[15px] h-[50px] justify-center items-center flex uppercase rounded-[11px]">
        Change Entry
      </a>
    </Link>
  </div>
);

const globalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 mr-2"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const MyEntry = (props) => {
  if (props.entry) {
    if (isEmpty(props.entry)) {
      return (
        <div className="text-xl text-center text-red-400 p-5">
          You have not made an entry
        </div>
      );
    } else {
      return (
        <div className="">
          <Entry players={props.entry} />
        </div>
      );
    }
  } else return null;
};

const MyPoolsTabList = (props) => {
  const { data: session }: any = useSession();
  const accessToken = session?.accessToken;
  const router = useRouter();
  const { isLoading: myPoolsLoading, data: myPools } = useQuery(
    "myPools",
    async () =>
      await fetch(`${API_URL as string}/pools/me`, {
        headers: {
          Authorization: `Bearer ${accessToken as string}`,
        },
      }).then(async (res) => await res.json()),
    {
      enabled: !!accessToken && !!router.isReady,
    },
  );

  const unapprovedPoolItem = (item) => {
    return (
      <li key={item.id} className="mr-2" role="presentation">
        <button
          className={returnTabStyle(item)}
          id="profile-tab"
          data-tabs-target="#profile"
          type="button"
          role="tab"
          aria-controls="profile"
          aria-selected="false"
        >
          <span className="flex">
            {item.approval_status === "PENDING" ? unapprovedIcon() : null}
            {item.approval_status === "NOT_APPROVED" ? rejectedIcon() : null}
            {}
            {item.name}
          </span>
        </button>
      </li>
    );
  };

  const tabItem = (item) => {
    return (
      <li key={item.id} className="mr-2" role="presentation">
        <Link href={`/pool/${item.id as string}`}>
          <button
            className={returnTabStyle(item)}
            id="profile-tab"
            data-tabs-target="#profile"
            type="button"
            role="tab"
            aria-controls="profile"
            aria-selected="false"
          >
            <span className="flex">
              {item.type === "GLOBAL" ? globalIcon() : null}
              {}
              {item.type === "GLOBAL" ? "Global Rank" : item.name}
            </span>
          </button>
        </Link>
      </li>
    );
  };

  const returnPoolTabType = (item) => {
    if (
      item.approval_status === "NOT_APPROVED" ||
      item.approval_status === "PENDING"
    ) {
      return unapprovedPoolItem(item);
    } else {
      return tabItem(item);
    }
  };

  const baseTabStyle =
    "inline-block py-1 px-4 text-lg text-center rounded-t-lg border-b-2 ";
  const currentPoolTabButtonStyle =
    baseTabStyle + " text-gray-900 border-gray-900";
  const nonCurrentPoolTabButtonStyle =
    baseTabStyle +
    "text-gray-500 border-transparent hover:text-gray-900 hover:border-gray-900 ";
  const notApprovedPoolTabButtonStyle =
    baseTabStyle + " border-transparent text-red-100 hover:cursor-default";

  const returnTabStyle = (item) => {
    if (item.id === props.currentSelectedPool) {
      return currentPoolTabButtonStyle;
    } else if (
      item.approval_status === "NOT_APPROVED" ||
      item.approval_status === "PENDING"
    ) {
      return notApprovedPoolTabButtonStyle;
    } else {
      return nonCurrentPoolTabButtonStyle;
    }
  };

  if (myPools !== null) {
    const globalPools = filter(
      myPools,
      (item) => item.type === "GLOBAL",
    ).filter((p) => p.tournamentId === props.poolData?.tournament?.id);
    const others = filter(myPools, (item) => item.type !== "GLOBAL").filter(
      (p) => p.tournamentId === props.poolData?.tournament?.id,
    );
    return (
      <div>
        <div className="border-b overflow-y-hidden overflow-x-auto border-gray-200 dark:border-gray-700">
          <ul
            className="flex flex-nowrap whitespace-nowrap "
            id="myTab"
            data-tabs-toggle="#myTabContent"
            role="tablist"
          >
            {!isEmpty(myPools) &&
              globalPools.map((item) => {
                return tabItem(item);
              })}
            {!isEmpty(myPools) &&
              others.map((item) => {
                return returnPoolTabType(item);
              })}
            {myPoolsLoading ? tabItem("None") : null}
          </ul>
        </div>
        <div id="myTabContent"></div>
      </div>
    );
  } else return null;
};

const OpenWidget = (data) => {
  let text = "";
  if (isAfter(Date.now(), new Date(data.tournament.date))) {
    text = "No time left";
  } else {
    text = formatDistanceToNowStrict(new Date(data.tournament.date), {
      unit: "hour",
    });
  }

  return (
    <div className="bg-[#016937] text-white font-medium text-[12px] py-[12px] px-[8px] text-center">
      <div>Time left for entries</div>
      <div className="text-[18px] font-bold uppercase leading-[22px]">
        {text}
      </div>
    </div>
  );
};

const InProgressWidget = (data) => {
  return (
    <div className="grid grid-cols-2 gap-y-2 gap-x-2 mb-2">
      <div className="bg-amber-400">
        <div className="mb-2">
          <ComponentHeader text="Rank" />
        </div>

        <div className="flex justify-center items-center text-6xl">
          {data?.position?.rank}
          <sup className="text-2xl">{ordinalValue(data?.position?.rank)}</sup>
        </div>
      </div>
      <div className="bg-amber-400">
        <ComponentHeader text="Score" />
        <div className="flex justify-center items-center text-6xl">
          {formatScore(data?.position?.score)}
        </div>
      </div>
    </div>
  );
};

export const PoolDetail = (props) => {
  const { data: session }: any = useSession();
  const accessToken = session?.accessToken;
  const router = useRouter();
  const queryClient = useQueryClient();
  const [userEntryToShowId, setUserEntryToShowId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [userEntryDisplayModal, setUserEntryDisplayModal] = useState(false);

  const discussionRef = useRef();

  // Why is useEffect being used here!?
  useEffect(() => {
    // check UserToPoolId and Action type
    if (router.query.utp && router.query.act) {
      const { utp, act } = router.query;
      // sample link : http://localhost:3000/pool/b39f951a-580d-438c-b787-09e7c607a133?utp=5844b544-e6c8-4e1d-a227-af49848a5c56&act=true
      if (act === "true") {
        approveUserMutation.mutate(utp as any);
      } else {
        rejectUserMutation.mutate(utp as any);
      }
    }

    return () => {
      // second
    };
  }, []);

  const {
    isLoading: poolDataLoading,
    data: poolData,
    refetch: refetchPoolData,
  } = useQuery(
    ["poolDetails", props.id],
    async () => await getPoolById(accessToken, props.id),
    {
      enabled: !!(accessToken as string) && !!router.isReady,
      retry: false, // Add custom retry function to retry if 500 (not FORBIDDEN)
    },
  );

  const {
    isLoading: userEntryIsLoading,
    error: userEntryError,
    data: userEntry,
  } = useQuery(
    ["userEntry", userEntryToShowId],
    async () => {
      return await getUserEntry(accessToken, userEntryToShowId);
    },
    {
      enabled: !!accessToken && !!router.isReady && !!userEntryDisplayModal,
      retry: false, // Add custom retry function to retry if 500 (not FORBIDDEN)
    },
  );

  if (userEntryError) {
    console.log("A userEntryError was found");
  }

  const userEntryShowPress = (userId) => {
    setUserEntryDisplayModal(true);
    setUserEntryToShowId(userId);
  };

  let tournamentStatus;
  let entry;

  if (poolData) {
    tournamentStatus = poolData?.tournament?.status;
    entry = poolData?.entry;
  }

  const approveUserMutation = useMutation(
    async (userToPoolId) =>
      await approveUserPoolJoinRequest(accessToken, userToPoolId),
    {
      onSuccess: (data, variables, context) => {
        void queryClient.invalidateQueries("poolDetails");
        void refetchPoolData();
        toast.success("The user has been approved");
      },
      onError: (error: Error) => {
        toast.error(
          `An error occurred whilst approve the user - ${error.message}`,
        );
      },
    },
  );

  const rejectUserMutation = useMutation(
    async (userToPoolId) =>
      await rejectUserPoolJoinRequest(accessToken, userToPoolId),
    {
      onSuccess: (data, variables, context) => {
        void queryClient.invalidateQueries("poolDetails");
        void refetchPoolData();
        toast.success("The user has been rejected");
      },
      onError: (error: Error) => {
        toast.error(
          `An error occurred whilst approve the user - ${error.message}`,
        );
      },
    },
  );

  const pendingGolfers = () => {
    return poolData?.pending_approval.map((item) => {
      return (
        <div
          key={item.user.id}
          className="flex flex-row items-center justify-between font-serif"
        >
          <div>
            <span>
              {item.user.first_name} {item.user.last_name}{" "}
            </span>
            <span className="hidden sm:inline text-xs">
              <Link href={`mailto:${item.user.email as string}`}>
                <a>({item.user.email})</a>
              </Link>
            </span>
          </div>
          <div>
            <button
              onClick={(e) => {
                e.preventDefault();
                approveUserMutation.mutate(item.userToPoolId);
              }}
              className="bg-green-400 text-white bold rounded text-xs p-1 mr-3"
            >
              Approve
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                rejectUserMutation.mutate(item.userToPoolId);
              }}
              className="bg-red-400 text-white bold  rounded text-xs p-1"
            >
              Reject
            </button>
          </div>
        </div>
      );
    });
  };

  const pendingApprovalBlock = () => (
    <div className="m-2 p-2 col-span-1 sm:col-span-2 border-4 border-red-300 bg-red-100">
      Users are pending approval
      {pendingGolfers()}
    </div>
  );

  const showPoolSummary = (data) => {
    if (tournamentStatus === "LOCKED" || tournamentStatus === "CLOSED") {
      return <InProgressWidget position={data.position} />;
    } else if (tournamentStatus === "OPEN") {
      return <OpenWidget {...poolData} />;
    } else {
      console.error("We are returning null, and shouldnt be ");
      return null;
    } // Maybe have a closed widget? Set up widget ? Not sure what states we allow for
  };

  const showMakeEntryButton = (poolId) => {
    if (tournamentStatus === "OPEN") {
      return makeEntryButton(poolId);
    }
  };

  const showEntryError = () => {
    return <div className="pt-10">Could not show you the entry</div>;
  };

  const showEntryModal = () => {
    let css =
      "fixed hidden inset-0 bg-gray-600 bg-opacity-80 overflow-y-auto max-h-full w-full";
    if (userEntryDisplayModal) {
      css =
        "fixed inset-0 bg-gray-600 z-50 bg-opacity-80 overflow-y-auto max-h-full w-full";
    }
    return (
      <div className={css} id="my-modal">
        <div className="flex flex-col relative top-5 mx-auto px-5 border w-9/12 shadow-lg rounded-md bg-white">
          <div className="flex-1 text-center pt-3">
            {userEntryIsLoading ? (
              <div className="flex justify-center">
                <Spinner width="80" height="80" fillColor="#000000" />
              </div>
            ) : null}
            {userEntryError ? showEntryError() : <Entry players={userEntry} />}
          </div>
          <div className="py-2 text-center">
            <button
              id="ok-btn"
              className="px-4 py-2 bg-green-500 text-white text-base font-medium rounded-md shadow-sm w-24 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300"
              onClick={(e) => {
                console.log("Entry pressed");
                setUserEntryDisplayModal(false);
                setUserEntryToShowId(null);
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const TournamentScoreboardLink = (tournamentId) => (
    <Link href={`/tournaments/${tournamentId as string}/scoreboard`}>
      <a className="flex items-center justify-center text-[12px] font-bold px-[32px] text-white h-[50px] bg-black rounded-[8px]">
        PLAYER LEADERBOARD
      </a>
    </Link>
  );

  const PrizeLink = (poolId, poolName) => (
    <Link href={`/prizes/${poolId as string}?poolName=${poolName as string}`}>
      <a className="flex items-center justify-center text-[12px] font-bold px-[32px]  text-white h-[50px] bg-[#016937] rounded-[8px]">
        PRIZE INFORMATION
      </a>
    </Link>
  );

  const DescriptionLink = () => (
    <a
      onClick={() => {
        alert(poolData?.description);
      }}
    >
      <span className="hover:cursor-pointer bg-black rounded block text-white text-2xl text-center p-2 pt-2 font-bold text-[16px] leading-tight uppercase hover:bg-gray-800 hover:shadow-lg focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg transition duration-150 ease-in-out mt-2">
        Pool Description
      </span>
    </a>
  );

  const showLastUpdated = () => {
    if (poolData?.lastUpdated) {
      const lastUpdated = new Date(poolData?.lastUpdated);
      const now = new Date();
      const message = formatDistance(lastUpdated, now, {
        addSuffix: true,
      });
      return (
        <span className="flex pr-2 items-center">
          <span className="pr-2">
            <Refresh width={5} height={5} />
          </span>
          Last updated {message}
        </span>
      );
    } else return null;
  };

  const title = (poolName) => (
    <Head>
      <title>{`GolfChamps | Pool - ${poolName}`}</title>
    </Head>
  );

  return (
    <>
      {poolData?.name && title(poolData?.name)}
      <Fragment>
        <ComponentContainer>
          <div className="flex justify-between text-white min-h-[104px] rounded-[8px] relative overflow-hidden">
            <div className="absolute w-full h-full z-0">
              <ImageWithFallback
                src={
                  props.picture ||
                  "https://stgolfchampsprodeastus.blob.core.windows.net/public/Default.jpg"
                }
                fallBackSrc="https://stgolfchampsprodeastus.blob.core.windows.net/public/Default.jpg"
                layout="fill"
                alt="Cover tournament image"
                unoptimized
              />
            </div>
            <div className="z-0 px-[32px] flex-1 items-center flex-row font-['Montserrat'] grid grid-cols-2 p-[25px] gap-y-[8px]">
              <div className="flex-1 col-span-2 lg:col-span-1">
                <div className="text-[25px] font-bold uppercase">
                  {poolData ? poolData.name : "Loading..."}
                </div>
                <div className="text-[12px] font-normal">
                  {poolData && showLastUpdated()}
                </div>
              </div>
              <div className="flex gap-x-[14px] col-span-2 lg:col-span-1 max-lg:justify-start justify-end flex-wrap gap-y-[8px]">
                {poolData?.tournament?.status === "LOCKED" &&
                  TournamentScoreboardLink(poolData?.tournament?.id)}
                {poolData?.id &&
                  poolData?.type === "GLOBAL" &&
                  PrizeLink(poolData?.id, poolData?.name)}
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="grid grid-cols-3 gap-[8px] pt-[10px]">
              <div className="col-span-3 md:col-span-1">
                {poolData?.id &&
                  !isEmpty(poolData.entry) &&
                  showPoolSummary(poolData)}
                <div>
                  {poolData?.id &&
                    poolData?.type !== "GLOBAL" &&
                    DescriptionLink()}
                </div>
              </div>
              <div className="col-span-3 md:col-span-2">
                <MyEntry entry={entry} />
                {poolData?.id && showMakeEntryButton(props.id)}
              </div>
            </div>
            <div className="">
              <MyPoolsTabList
                currentSelectedPool={props.id}
                poolData={poolData}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2">
              {poolData?.pending_approval &&
              poolData?.pending_approval.length > 0
                ? pendingApprovalBlock()
                : null}
              {showEntryModal()}

              {poolDataLoading && (
                <div>
                  <div className='h-[50px] bg-black text-white flex items-center px-[12px] font-["Montserrat"] justify-between rounded-[5px]'>
                    <span className="font-bold text-[16px]">
                      {props.tournament?.status === "OPEN"
                        ? "Pool Entrants"
                        : "LEADERBOARD"}
                    </span>
                    <span className="font-normal text-[12px]">
                      {props.poolData?.userCount || 0} USERS
                    </span>
                  </div>
                  <div>
                    <div className="flex justify-center mt-2">
                      <GolfSpinner width="30" height="30" />
                    </div>
                  </div>
                </div>
              )}
              {!poolDataLoading &&
                poolData?.id &&
                (poolData?.tournament?.status === "LOCKED" ||
                  poolData?.tournament?.status === "OPEN" ||
                  poolData?.tournament?.status === "SETUP") && (
                  <div>
                    <Leaderboard
                      id={props.id}
                      tournament={poolData?.tournament}
                      userCount={poolData?.userCount || 0}
                      showEntryPress={(id) => {
                        if (poolData?.tournament?.status === "LOCKED") {
                          userEntryShowPress(id);
                        }
                      }}
                      discussionRef={discussionRef}
                    />
                    <BlockAdSlot />
                  </div>
                )}
              <div>
                <Discussion poolId={props.id} discussionRef={discussionRef} />
              </div>
            </div>
          </div>
        </ComponentContainer>
        {!poolDataLoading && poolData === undefined && (
          <>
            <div
              className="fixed inset-0 bg-gray-600 z-50 bg-opacity-80 overflow-y-auto h-full w-full"
              id="my-modal"
            >
              <div className="flex  flex-col relative top-40 md:top-20 mx-auto px-5 border w-9/12 md:w-5/12 shadow-lg rounded-md bg-white">
                <div className="flex-1 text-2xl text-center pt-12 pb-6">
                  you haven't been approved to view that pool
                </div>
                <div className="py-6 text-center">
                  <Link href={"/pools"}>
                    <button
                      type="button"
                      id="ok-btn"
                      className="px-4 py-2 bg-green-500 text-white text-base font-medium rounded-md shadow-sm w-24 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300"
                    >
                      Go Back
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </Fragment>
    </>
  );
};
